import { CalendarCheck, ClipboardList, HardHat, MessagesSquare, PencilRuler, ShieldCheck } from "lucide-react";
import { Reveal, spotlight } from "@/components/Reveal";
import QuoteForm from "@/components/QuoteForm";
import IMG from "@/lib/images";

const PILLARS = [
    { icon: MessagesSquare, title: "Communication & dependable scheduling", desc: "One point of contact, confirmations in writing, and crews that show up when we say they will — coordinated around your other trades." },
    { icon: ClipboardList, title: "Detailed estimates & defined scope", desc: "Itemized subcontract quotes based on your drawings or a site walk: surfaces, coats, products, prep and schedule — no vague line items." },
    { icon: PencilRuler, title: "Preparation & consistent finishing standards", desc: "Documented prep checklist on every job — repairs, sanding, caulking, priming — and the same finish standard from unit 1 to unit 100." },
    { icon: HardHat, title: "Job-site discipline", desc: "Tidy, safe work areas, daily cleanup, and punch lists closed before we leave. Your site reflects your company — and ours." },
];

const RFQ = [
    ["01", "Send project details", "Share drawings, scope or a site walk date through the form below or by email."],
    ["02", "We estimate", "Takeoff and site review, then a written, itemized subcontract quote with products and schedule."],
    ["03", "Walk-through & award", "Align on scope, timeline and site rules. Contract signed, crews booked."],
    ["04", "Paint & close out", "Prep, paint, daily tidy, final walkthrough and punch list closed with your site supervisor."],
];

export default function Builders() {
    return (
        <div data-testid="builders-page" className="pt-28 lg:pt-36">
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <div className="grid items-center gap-14 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Builders &amp; Contractors</p>
                        <h1 className="font-display mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                            A painting subcontractor <span className="text-gold">you don't have to chase.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
                            For general contractors, custom home builders, renovation companies,
                            developers and property managers across the GTA: residential,
                            new-construction, renovation and commercial painting packages with the
                            communication and schedule discipline your sites run on.
                        </p>
                        <div className="mt-8 grid gap-4 sm:grid-cols-2">
                            {PILLARS.map((p, i) => (
                                <div key={p.title} data-testid={`builders-pillar-${i}`} onMouseMove={spotlight} className="spot-card h-full border border-gold/15 bg-ink-3 p-6">
                                    <p.icon size={22} className="text-gold" />
                                    <h3 className="font-display mt-4 text-base font-bold leading-snug">{p.title}</h3>
                                    <p className="mt-2 text-xs leading-relaxed text-slate-400">{p.desc}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-5">
                        <div className="clip-frame overflow-hidden">
                            <img src={IMG.newBuildWorks} alt="New construction exterior painting in progress" className="h-[440px] w-full object-cover lg:h-[560px]" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-y border-gold/15 bg-ink-2 mt-20">
                <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Subcontract RFQ process</p>
                        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                            From drawings to painted, in four steps.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {RFQ.map(([n, t, d], i) => (
                            <Reveal key={n} delay={i * 0.08}>
                                <div data-testid={`builders-rfq-step-${i}`} className="h-full border border-gold/15 bg-ink-3 p-8">
                                    <span className="font-mono text-sm text-gold">{n}</span>
                                    <h3 className="font-display mt-5 text-xl font-bold">{t}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p data-testid="builders-qualifications-note" className="placeholder-block mt-10 p-5 text-xs leading-relaxed text-slate-300">
                            <span className="font-mono uppercase tracking-[0.2em] text-gold">Licences & coverage — </span>
                            [Placeholders: add your WSIB clearance, liability insurance, bonding status
                            and workforce details here once confirmed — they are intentionally not
                            claimed until you provide them.]
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16">
                <div className="grid items-start gap-14 border border-gold/15 bg-ink-3 p-8 sm:p-12 lg:grid-cols-2 lg:p-16">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Contractor inquiry</p>
                        <h2 className="font-display mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                            Request a subcontractor quote.
                        </h2>
                        <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-300">
                            Tell us about the project: location, size, timeline and scope. You'll get
                            a written, itemized quote — typically within a few business days,
                            depending on drawings and site access.
                        </p>
                        <ul className="mt-8 space-y-3 text-sm text-slate-300">
                            {["New construction & custom homes", "Renovation & repaint packages", "Multi-unit & property-management programs", "Commercial & office fit-outs"].map((x) => (
                                <li key={x} className="flex items-center gap-3">
                                    <ShieldCheck size={15} className="shrink-0 text-gold" /> {x}
                                </li>
                            ))}
                        </ul>
                        <div className="clip-frame mt-10 overflow-hidden">
                            <img src={IMG.architectural} alt="Construction project in the GTA" className="h-56 w-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <QuoteForm source="contractor" />
                    </Reveal>
                </div>
            </section>

            <div className="h-8" aria-hidden="true">
                <CalendarCheck className="mx-auto text-gold/30" size={20} />
            </div>
        </div>
    );
}